/* Track history trails.
 *
 * v0.9: trails are back-projected from current course and speed, because there is no
 * engine yet to have recorded a history. In v1.0 they come from the event stream, which
 * is the whole point of having one (ARCHITECTURE.md §10.2).
 *
 * The back-projection is deliberately dead-straight. A synthetic trail with invented
 * manoeuvres would be fabricating history the operator might read something into.
 */
import type { FeatureCollection, LineString } from 'geojson'
import { destination, nmToMetres } from './geodesy'
import type { OrbisContact, OrbisEntity } from '../data/types'

/** Minutes of history to draw, and how many vertices to draw it with. */
// 10 minutes: long enough to read a heading, short enough that a 420 kt fighter's
// trail does not stripe across the whole theatre and look like a rendering error.
const HISTORY_MIN = 10
const STEPS = 10

interface TrailProps {
  affiliation: string
  entityId: string
  kind: 'entity' | 'contact'
}

function backTrack(
  lon: number,
  lat: number,
  courseDeg: number,
  speedKt: number,
): [number, number][] {
  const coords: [number, number][] = []
  const reciprocal = (courseDeg + 180) % 360
  for (let i = STEPS; i >= 0; i--) {
    const minutes = (HISTORY_MIN * i) / STEPS
    const nm = (speedKt * minutes) / 60
    const p = destination({ lon, lat }, reciprocal, nmToMetres(nm))
    coords.push([p.lon, p.lat])
  }
  return coords
}

export function trails(
  entities: OrbisEntity[],
  contacts: OrbisContact[],
  includeEntities: boolean,
  includeContacts: boolean,
): FeatureCollection<LineString, TrailProps> {
  const features: FeatureCollection<LineString, TrailProps>['features'] = []

  if (includeEntities) {
    for (const e of entities) {
      if (e.speedKt < 1) continue // static facilities have no track
      features.push({
        type: 'Feature',
        properties: { affiliation: e.affiliation, entityId: e.id, kind: 'entity' },
        geometry: { type: 'LineString', coordinates: backTrack(e.lon, e.lat, e.courseDeg, e.speedKt) },
      })
    }
  }

  if (includeContacts) {
    for (const c of contacts) {
      // No course/speed solution means no track to draw. A bearing-only contact
      // genuinely has no history, and inventing one would be a lie.
      if (c.courseDeg === undefined || c.speedKt === undefined || c.speedKt < 1) continue
      features.push({
        type: 'Feature',
        properties: { affiliation: c.affiliation, entityId: c.id, kind: 'contact' },
        geometry: { type: 'LineString', coordinates: backTrack(c.lon, c.lat, c.courseDeg, c.speedKt) },
      })
    }
  }

  return { type: 'FeatureCollection', features }
}
